import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import Breadcrumb from "../../components/Breadcrumb";
import CourseService from "../../service/courseService";
import { resourceFileUrl, youtubeEmbedUrl } from "../../utils/courseResources";
import "./CourseDetails.css";
import ResourceVideoModal from "./ResourceVideoModal";

const CourseDetailsMain = () => {
  const [params] = useSearchParams();
  const courseId = params.get("courseId");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [modalVideo, setModalVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [relatedStatus, setRelatedStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();
    const options = { signal: controller.signal };
    setData(null);
    setLoading(true);
    setError("");
    setSelectedVideo(null);
    setModalVideo(null);
    setRelated([]);
    setRelatedStatus("loading");
    if (!courseId) {
      setLoading(false);
      return () => controller.abort();
    }

    const load = async () => {
      try {
        const [course, resources, types] = await Promise.all([
          CourseService.getCourse(courseId, options),
          CourseService.getResourcesByCourse(courseId, options),
          CourseService.ressourcesTypes(options),
        ]);
        if (controller.signal.aborted) return;
        if (!Array.isArray(resources) || !Array.isArray(types)) throw new Error("Invalid response");
        const groups = types.map(type => ({ ...type, resources: resources.filter(resource => String(resource.resource_type_id) === String(type.id)) }));
        const other = resources.filter(resource => !types.some(type => String(type.id) === String(resource.resource_type_id)));
        if (other.length) groups.push({ id: "other", name: "Autres ressources", resources: other });
        const videos = resources.filter(resource => youtubeEmbedUrl(resource.file_path));
        setData({ course, groups: groups.filter(group => group.resources.length), resources });
        setSelectedVideo(videos[0] || null);
        setLoading(false);
        const levelId = course.academic_level_id || course.academic_level?.id;
        if (!levelId) { setRelatedStatus("empty"); return; }
        try {
          const courses = await CourseService.listCoursesByAcademicLevel(levelId, options);
          if (controller.signal.aborted) return;
          const suggestions = courses.filter(item => String(item.id) !== String(courseId)).slice(0, 3);
          setRelated(suggestions);
          setRelatedStatus(suggestions.length ? "ready" : "empty");
        } catch {
          if (!controller.signal.aborted) setRelatedStatus("error");
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setError(error.response?.status === 404 ? "Ce cours est introuvable." : "Impossible de charger ce cours et ses ressources.");
          setLoading(false);
        }
      }
    };
    load();
    return () => controller.abort();
  }, [courseId, attempt]);

  return (
    <main>
      <Breadcrumb title="Détails du cours" subTitle="Cours" />
      <div className="it-course-details-area pt-120 pb-100">
        <div className="container">
          {!courseId ? <p>Sélectionnez un cours dans <Link to="/course-1">la liste des cours</Link>.</p>
            : loading ? <p role="status">Chargement du cours…</p>
            : error ? <div><p role="alert">{error}</p><button className="ed-btn-theme" onClick={() => setAttempt(value => value + 1)}>Réessayer</button></div>
            : data && <div className="row g-4">
              <div className="col-xl-9 col-lg-8">
                <div className="it-course-details-wrap">
                  <div className="it-evn-details-thumb mb-35">
                    {selectedVideo ? <>
                      <div className="course-video-frame">
                        <iframe key={selectedVideo.id} src={youtubeEmbedUrl(selectedVideo.file_path)} title={selectedVideo.name || data.course.name} allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                      </div>
                      <p className="mt-15">{selectedVideo.name}</p>
                    </> : <div className="course-video-empty">Aucune vidéo YouTube disponible pour ce cours.</div>}
                  </div>
                  <h2 className="it-evn-details-title mb-0 pb-5">{data.course.name}</h2>
                  <div className="postbox__meta"><span>{data.course.academic_level?.name}</span></div>
                  {data.course.description && <p>{data.course.description}</p>}
                  {data.groups.length ? <Tabs key={courseId}>
                    <div className="it-course-details-nav pb-30">
                      <TabList className="nav nav-tab course-resource-tabs">
                        {data.groups.map(group => <Tab key={group.id}>{group.name} ({group.resources.length})</Tab>)}
                      </TabList>
                    </div>
                    {data.groups.map(group => <TabPanel key={group.id}>
                      <ul className="course-resource-list">
                        {group.resources.map(resource => {
                          const videoUrl = youtubeEmbedUrl(resource.file_path);
                          const fileUrl = resourceFileUrl(resource);
                          const isVideo = group.format === "video" || resource.resource_type?.format === "video" || videoUrl;
                          const label = <>
                            <i className={isVideo ? "fa-solid fa-circle-play" : "fa-solid fa-file-pdf"} aria-hidden="true" />
                            <span>{resource.name}</span>
                            <span className="visually-hidden">{isVideo ? " — ouvrir la vidéo" : " — ouvrir le PDF dans un nouvel onglet"}</span>
                          </>;
                          return <li key={resource.id}>
                            {videoUrl ? (
                              <button type="button" className="course-resource-name course-resource-action" aria-haspopup="dialog" onClick={() => setModalVideo(resource)}>{label}</button>
                            ) : !isVideo && fileUrl ? (
                              <a className="course-resource-name course-resource-action" href={fileUrl} target="_blank" rel="noopener noreferrer">{label}</a>
                            ) : (
                              <><span className="course-resource-name"><i className={isVideo ? "fa-solid fa-circle-play" : "fa-solid fa-file-pdf"} aria-hidden="true" />{resource.name}</span><span>{isVideo ? "Vidéo indisponible" : "Document indisponible"}</span></>
                            )}
                          </li>;
                        })}
                      </ul>
                    </TabPanel>)}
                  </Tabs> : <p>Aucune ressource disponible pour ce cours.</p>}
                </div>
              </div>
              <aside className="col-xl-3 col-lg-4">
                <div className="it-evn-sidebar-box it-course-sidebar-box">
                  <h2 className="h5 mb-25">Cours du même niveau</h2>
                  {relatedStatus === "loading" && <p role="status">Chargement…</p>}
                  {relatedStatus === "empty" && <p>Aucun autre cours disponible pour ce niveau.</p>}
                  {relatedStatus === "error" && <p role="alert">Impossible de charger les cours associés.</p>}
                  <div className="it-evn-sidebar-list"><ul>
                  {related.map(course => <li key={course.id}><Link className="course-related-link" to={`/course-details?courseId=${encodeURIComponent(course.id)}`}>
                    <strong>{course.name}</strong><span>{data.course.academic_level?.name}</span>
                  </Link></li>)}
                  </ul></div>
                  <Link to="/course-1">Voir tous les cours</Link>
                </div>
              </aside>
            </div>}
        </div>
      </div>
      {modalVideo && <ResourceVideoModal resource={modalVideo} onClose={() => setModalVideo(null)} />}
    </main>
  );
};

export default CourseDetailsMain;
